// apps/client/src/pages/leads/website/WebsiteRoutingPreview.tsx
//
// "Where do this form's leads go?" for one website form, from
// previewWebsiteFormRouting (shared-schemas): the same matchers the router
// uses, run over the tenant's routing rules and routing_v2 mode. A form no
// rotation can take used to save silently and leave every enquiry
// unassigned (Cavara stage: company-wide form, head office only rotation).

import React from 'react';
import { AlertTriangle, ArrowRight, Info } from 'lucide-react';
import {
  previewWebsiteFormRouting,
  type RoutingPreview,
  type RoutingPreviewRules,
  type RoutingPreviewTarget,
} from '@valutten/shared-schemas/dist/websiteRoutingPreview';

/** What the page loaded for the preview. `status: 'error'` = rules unreadable. */
export interface WebsiteRoutingContext {
  status: 'loading' | 'ready' | 'error';
  /** Null = the tenant has no routing rules doc (no rotations at all). */
  rules: RoutingPreviewRules | null;
  routingV2Mode: unknown;
}

export interface RoutingPreviewLine {
  tone: 'ok' | 'info' | 'warn' | 'error';
  text: string;
}

const names = (list: ReadonlyArray<{ name: string }>) => list.map((r) => r.name).join(', ');

/** Plain-English lines for a preview. Pure, so the copy is unit tested. */
export function describeRoutingPreview(p: RoutingPreview): RoutingPreviewLine[] {
  if (p.kind === 'territory_triage') {
    return [
      {
        tone: 'warn',
        text:
          'Postcode territory routing is on and website forms collect no postcode, so these leads go to head office triage'
          + (p.fallbackOwnerCount > 0 ? ' and your fallback people.' : '.'),
      },
    ];
  }
  const lines: RoutingPreviewLine[] = [];
  if (p.kind === 'rotation') {
    lines.push({ tone: 'ok', text: `Leads go to the ${p.rotation.name} rotation.` });
  } else {
    lines.push({ tone: p.fallbackOwnerCount > 0 ? 'warn' : 'error', text: 'No rotation takes these leads.' });
    lines.push({
      tone: p.fallbackOwnerCount > 0 ? 'warn' : 'error',
      text:
        p.fallbackOwnerCount > 0
          ? 'They go to your fallback people.'
          : 'They will sit unassigned until someone picks them up in the Inbox. Add a rotation or choose fallback people on the Routing page.',
    });
  }
  if (p.answerRotations.length > 0) {
    lines.push({ tone: 'info', text: `Some answers send leads to: ${names(p.answerRotations)}.` });
  }
  if (p.ignoredAnswerRotations.length > 0) {
    lines.push({
      tone: 'warn',
      text: `${names(p.ignoredAnswerRotations)} match on answers and only take effect when answer-based routing is on (enforce).`,
    });
  }
  return lines;
}

const TONE_CLASS: Record<RoutingPreviewLine['tone'], string> = {
  ok: 'text-emerald-300',
  info: 'text-gray-300',
  warn: 'text-amber-300',
  error: 'text-red-300',
};

export const WebsiteRoutingPreview: React.FC<{
  context: WebsiteRoutingContext | null | undefined;
  target: RoutingPreviewTarget;
  testId?: string;
}> = ({ context, target, testId }) => {
  if (!context || context.status === 'loading') return null;
  if (context.status === 'error') {
    return (
      <p className="text-[0.6875rem] text-gray-500 flex items-start gap-1" data-testid={testId}>
        <Info className="w-3.5 h-3.5 mt-px flex-shrink-0" />
        Where these leads go could not be checked. Open the Routing page to confirm a rotation takes them.
      </p>
    );
  }
  const lines = describeRoutingPreview(previewWebsiteFormRouting(context.rules, context.routingV2Mode, target));
  const worst = lines.some((l) => l.tone === 'error') ? 'error' : lines.some((l) => l.tone === 'warn') ? 'warn' : 'ok';
  const box =
    worst === 'error'
      ? 'border-red-500/30 bg-red-500/10'
      : worst === 'warn'
        ? 'border-amber-500/30 bg-amber-500/10'
        : 'border-gray-700 bg-gray-900/40';
  const Icon = worst === 'ok' ? ArrowRight : AlertTriangle;
  return (
    <div className={`rounded-md border px-3 py-2 text-xs flex items-start gap-2 ${box}`} data-testid={testId} role={worst === 'ok' ? undefined : 'status'}>
      <Icon className={`w-3.5 h-3.5 mt-px flex-shrink-0 ${TONE_CLASS[worst]}`} />
      <div className="space-y-0.5">
        {lines.map((l) => (
          <p key={l.text} className={TONE_CLASS[l.tone]}>{l.text}</p>
        ))}
      </div>
    </div>
  );
};

export default WebsiteRoutingPreview;
